import {
  getExpectedFrequency,
  getNoteName,
  IN_TUNE_CENTS,
  SLIGHT_OFF_CENTS,
  TOTAL_KEYS,
} from './constants';
import type { KeyData } from './detector';

export interface KeyReport {
  keyNum: number;
  noteName: string;
  detectedHz: number;
  expectedHz: number;
  cents: number;
  status: 'In Tune' | 'Slightly Off' | 'Out of Tune';
}

export interface TuningReport {
  keys: KeyReport[];
  avgAbsDeviation: number;
  keysInTune: number;
  keysSlightlyOff: number;
  keysOutOfTune: number;
  overallStatus: string;
}

export function generateReport(data: KeyData[]): TuningReport {
  const keys: KeyReport[] = data.map((d) => {
    const expectedHz = getExpectedFrequency(d.keyNum);
    const cents = 1200 * Math.log2(d.frequency / expectedHz);
    const absCents = Math.abs(cents);

    let status: KeyReport['status'];
    if (absCents <= IN_TUNE_CENTS) {
      status = 'In Tune';
    } else if (absCents <= SLIGHT_OFF_CENTS) {
      status = 'Slightly Off';
    } else {
      status = 'Out of Tune';
    }

    return {
      keyNum: d.keyNum,
      noteName: getNoteName(d.keyNum),
      detectedHz: Math.round(d.frequency * 100) / 100,
      expectedHz: Math.round(expectedHz * 100) / 100,
      cents: Math.round(cents * 10) / 10,
      status,
    };
  });

  const keysInTune = keys.filter((k) => k.status === 'In Tune').length;
  const keysSlightlyOff = keys.filter(
    (k) => k.status === 'Slightly Off',
  ).length;
  const keysOutOfTune = keys.filter((k) => k.status === 'Out of Tune').length;

  const avgAbsDeviation =
    keys.length > 0
      ? Math.round(
          (keys.reduce((sum, k) => sum + Math.abs(k.cents), 0) / keys.length) *
            10,
        ) / 10
      : 0;

  let overallStatus: string;
  if (keysOutOfTune === 0 && keysSlightlyOff <= 5) {
    overallStatus = 'Your piano is in great shape! 🎉';
  } else if (keysOutOfTune <= 10) {
    overallStatus =
      'Your piano is mostly in tune with a few keys needing attention.';
  } else if (keysOutOfTune <= 30) {
    overallStatus = 'Your piano could benefit from a professional tuning.';
  } else {
    overallStatus =
      'Your piano needs a professional tuning. Consider scheduling a visit!';
  }

  return {
    keys,
    avgAbsDeviation,
    keysInTune,
    keysSlightlyOff,
    keysOutOfTune,
    overallStatus,
  };
}

export function renderReport(report: TuningReport): void {
  // Summary text
  const summaryEl = document.getElementById('report-summary')!;
  summaryEl.textContent = report.overallStatus;

  // Stats cards
  const statsEl = document.getElementById('report-stats')!;
  statsEl.innerHTML = `
    <div class="stat-card">
      <span class="stat-value status-intune">${report.keysInTune}</span>
      <span class="stat-label">In Tune</span>
    </div>
    <div class="stat-card">
      <span class="stat-value status-slight">${report.keysSlightlyOff}</span>
      <span class="stat-label">Slightly Off</span>
    </div>
    <div class="stat-card">
      <span class="stat-value status-offtune">${report.keysOutOfTune}</span>
      <span class="stat-label">Out of Tune</span>
    </div>
  `;

  // Table
  const tbody = document.getElementById('report-body')!;
  tbody.innerHTML = '';

  for (const key of report.keys) {
    const tr = document.createElement('tr');
    const statusClass =
      key.status === 'In Tune'
        ? 'status-intune'
        : key.status === 'Slightly Off'
          ? 'status-slight'
          : 'status-offtune';

    const centsDisplay = key.cents > 0 ? `+${key.cents}` : `${key.cents}`;

    tr.innerHTML = `
      <td>${key.keyNum}</td>
      <td><strong>${key.noteName}</strong></td>
      <td>${key.detectedHz}</td>
      <td>${key.expectedHz}</td>
      <td class="${statusClass}">${centsDisplay}</td>
      <td class="${statusClass}">${key.status}</td>
    `;
    tbody.appendChild(tr);
  }
}
